/**
 * Intelligence API Requests
 * 
 * API layer for competitor intelligence features:
 * - Demo analysis (unauthenticated)
 * - Tracked competitors
 * - Intelligence feed
 * - Battlecards
 * - Change timeline
 */

import { getClient } from './base/api';

const client = getClient();

// ============================================
// DEMO / PUBLIC ANALYSIS
// ============================================

/**
 * Start a demo analysis for a competitor (unauthenticated)
 * @param {string} input - URL or company name
 * @param {string} fingerprint - Browser fingerprint for rate limiting
 * @returns {Promise} Analysis result with company data
 */
export const startDemoAnalysis = async (input, fingerprint = null) => {
  return client.post('intel/demo/analyze', {
    input,
    fingerprint
  });
};

/**
 * Get demo analysis result by ID
 * @param {string} demoId - Demo analysis ID
 * @returns {Promise} Analysis data
 */
export const getDemoAnalysisResult = async (demoId) => {
  return client.get(`intel/demo/${demoId}`);
};

// ============================================
// TRACKED COMPETITORS
// ============================================

/**
 * Get all tracked competitors for the current organization
 * @returns {Promise} List of tracked competitors
 */
export const getTrackedCompetitors = async () => {
  return client.get('intel/competitors');
};

/** Competitive-position grid: aspect tree + per-company scores/coverage (real). */
export const getAspectGrid = async () => {
  return client.get('intel/aspect-grid');
};

/**
 * Add a new competitor to track
 * @param {string} domain - Competitor domain (e.g., "notion.so")
 * @param {string} name - Company name
 * @param {string} priority - Priority level (critical/high/medium/low)
 * @returns {Promise} Created tracked competitor
 */
export const addTrackedCompetitor = async (domain, name = null, priority = 'medium') => {
  return client.post('intel/competitors', {
    domain,
    name,
    priority
  });
};

/**
 * Remove a competitor from tracking
 * @param {number} trackedId - Tracked competitor ID
 * @returns {Promise} Success response
 */
export const removeTrackedCompetitor = async (trackedId) => {
  return client.delete(`intel/competitors/${trackedId}`);
};

/**
 * Update tracked competitor settings
 * @param {number} trackedId - Tracked competitor ID
 * @param {object} updates - Fields to update (priority, notes, alert_on_changes, etc.)
 * @returns {Promise} Updated competitor
 */
export const updateTrackedCompetitor = async (trackedId, updates) => {
  return client.patch(`intel/competitors/${trackedId}`, updates);
};

// ============================================
// COMPETITOR DETAILS / ANALYSIS
// ============================================

/**
 * Get full analysis for a specific competitor
 * @param {number} companyId - Company ID
 * @returns {Promise} Company analysis with snapshots, changes, trends
 */
export const getCompetitorAnalysis = async (companyId) => {
  return client.get(`intel/companies/${companyId}/analysis`);
};

/**
 * Get competitor change timeline
 * @param {number} companyId - Company ID
 * @param {number} limit - Number of changes to fetch
 * @returns {Promise} List of detected changes
 */
export const getCompetitorTimeline = async (companyId, limit = 50) => {
  return client.get(`intel/companies/${companyId}/timeline`, {
    params: { limit }
  });
};

/**
 * Get pricing history for a competitor
 * @param {number} companyId - Company ID
 * @param {number} days - Number of days of history
 * @returns {Promise} Pricing snapshots
 */
export const getCompetitorPricingHistory = async (companyId, days = 90) => {
  return client.get(`intel/companies/${companyId}/pricing-history`, {
    params: { days }
  });
};

/**
 * Manually trigger a fresh deep/fast scan for a tracked company
 * (or your own product). Always returns the full scan_status payload
 * (in-flight, cooldown, plan, ETA) so the caller can render a single
 * UI state machine for click responses and background polling.
 * @param {number} companyId - Company ID
 * @returns {Promise} { status: 'triggered' | 'skipped' | 'rejected',
 *                      reason, scan_status }
 */
export const refreshCompanyAnalysis = async (companyId) => {
  return client.post(`intel/companies/${companyId}/refresh`);
};

/**
 * Read current scan status for a company:
 *   - Which scan type the user's plan gets (deep vs fast) + ETA
 *   - Whether a scan is in-flight right now
 *   - Last analysis timestamp + source
 *   - Whether the user can rescan now, or how many seconds remain
 * UI calls this on mount and polls every 15s while in_flight.
 * @param {number} companyId - Company ID
 * @returns {Promise} scan_status payload
 */
export const getScanStatus = async (companyId) => {
  return client.get(`intel/companies/${companyId}/scan-status`);
};

/**
 * Get trend summary for a competitor
 * @param {number} companyId - Company ID
 * @param {string} period - Time period (last_7_days, last_30_days, last_90_days)
 * @returns {Promise} Trend summary data
 */
export const getCompetitorTrends = async (companyId, period = 'last_30_days') => {
  return client.get(`intel/companies/${companyId}/trends`, {
    params: { period }
  });
};

// ============================================
// INTELLIGENCE FEED
// ============================================

/**
 * Get the intelligence feed for the current organization
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {boolean} includeDismissed - Include dismissed items
 * @returns {Promise} Feed items
 */
export const getIntelligenceFeed = async (page = 1, limit = 20, includeDismissed = false) => {
  return client.get('intel/feed', {
    params: { page, limit, include_dismissed: includeDismissed }
  });
};

/**
 * Mark a feed item as read
 * @param {number} changeId - Change ID
 * @returns {Promise} Success response
 */
export const markFeedItemRead = async (changeId) => {
  return client.post(`intel/feed/${changeId}/read`);
};

/**
 * Dismiss a feed item
 * @param {number} changeId - Change ID
 * @returns {Promise} Success response
 */
export const dismissFeedItem = async (changeId) => {
  return client.post(`intel/feed/${changeId}/dismiss`);
};

/**
 * Get unread count for the intelligence feed
 * @returns {Promise} Unread count
 */
export const getUnreadCount = async () => {
  return client.get('intel/feed/unread-count');
};

// ============================================
// BATTLECARDS
// ============================================

/**
 * Get battlecard for a competitor
 * @param {number} companyId - Company ID
 * @returns {Promise} Battlecard data
 */
export const getBattlecard = async (companyId) => {
  return client.get(`intel/battlecards/${companyId}`);
};

/**
 * Request battlecard regeneration
 * @param {number} companyId - Company ID
 * @returns {Promise} Job ID for tracking
 */
export const regenerateBattlecard = async (companyId) => {
  return client.post(`intel/battlecards/${companyId}/regenerate`);
};

/**
 * Get all battlecards for the organization
 * @returns {Promise} List of battlecards
 */
export const getAllBattlecards = async () => {
  return client.get('intel/battlecards');
};

export const shareBattlecard = async (companyId) => {
  return client.post(`intel/battlecards/${companyId}/share`);
};

export const exportBattlecard = async (companyId) => {
  return client.get(`intel/battlecards/${companyId}/export`);
};

// ============================================
// DASHBOARD / OVERVIEW
// ============================================

/**
 * Get intelligence dashboard overview
 * @returns {Promise} Dashboard data with summary stats
 */
export const getIntelligenceDashboard = async () => {
  return client.get('intel/dashboard');
};

/**
 * Get recent high-impact changes across all competitors
 * @param {number} limit - Number of changes
 * @returns {Promise} List of high-impact changes
 */
export const getHighImpactChanges = async (limit = 10) => {
  return client.get('intel/dashboard/high-impact', {
    params: { limit }
  });
};

// ============================================
// ONBOARDING
// ============================================

export const discoverCompetitors = async (companyUrl) => {
  return client.post('intel/onboarding/discover-competitors', {
    company_url: companyUrl
  });
};

export const completeOnboardingSetup = async (companyUrl, competitors, alertPreferences) => {
  return client.post('intel/onboarding/complete-setup', {
    company_url: companyUrl,
    competitors,
    alert_preferences: alertPreferences
  });
};

export const getOnboardingStatus = async () => {
  return client.get('intel/onboarding/status');
};

export const getPlanInfo = async () => {
  return client.get('intel/plan');
};

export const analyzeAllCompetitors = async () => {
  return client.post('intel/competitors/analyze-all');
};

// ============================================
// SEARCH / DISCOVERY
// ============================================

/**
 * Search for companies (for adding new competitors)
 * @param {string} query - Search query
 * @returns {Promise} List of matching companies
 */
export const searchCompanies = async (query) => {
  return client.get('intel/companies/search', {
    params: { q: query }
  });
};

/**
 * Discover competitor suggestions from dashboard (rate-limited by plan)
 * @param {string} companyUrl - Optional company URL (uses org default if omitted)
 * @returns {Promise} Suggestions with discover usage info
 */
export const discoverCompetitorSuggestions = async (companyUrl = null) => {
  return client.post('intel/competitors/discover', {
    company_url: companyUrl
  });
};

/**
 * Update the user's own company domain
 * @param {string} domain - The company domain
 * @returns {Promise} Updated company info
 */
export const updateMyCompanyDomain = async (domain) => {
  return client.post('intel/my-company', { domain });
};

// ============================================
// LINKEDIN POSTS
// ============================================

export const getLinkedInPosts = async (week = null) => {
  const params = week ? { week } : {};
  return client.get('intel/linkedin-posts', { params });
};

export const generateLinkedInPosts = async (force = false) => {
  return client.post('intel/linkedin-posts/generate', { force });
};

export const updateLinkedInPostStatus = async (postId, status) => {
  return client.patch(`intel/linkedin-posts/${postId}/status`, { status });
};

// ============================================
// CREDIT TRACKING / BETA UPGRADE
// ============================================

/**
 * Snapshot of the org's Evaligo credit usage for the org-settings UI.
 * Returns { credit_tracking_enabled, plan_name, credits_cap,
 *           total_credits_used, is_test_account, breakdown_this_month,
 *           recent_calls, can_request_upgrade, days_until_can_request }.
 * Safe to call when the credit infra hasn't been migrated yet — the
 * backend returns sensible defaults with credit_tracking_enabled=false.
 */
export const getCreditUsage = async () => {
  return client.get('intel/usage');
};

/**
 * Self-serve kill switch for the calling org: stop/resume ALL spending flows.
 * @param {boolean} paused
 */
export const setSpendingPause = async (paused) => {
  return client.post('intel/spending-pause', { paused });
};

/**
 * Customer clicks "Request beta upgrade" in the 402 modal.
 * @param {string} reason - free-text textarea content
 * @returns {Promise} { status, request_id, message } or 429 cooldown
 */
export const requestBetaUpgrade = async (reason) => {
  return client.post('intel/upgrade-request', { reason });
};

/**
 * Admin: list pending (or all) upgrade requests.
 * @param {string} status - 'pending' | 'approved' | 'rejected' | 'all'
 */
export const listUpgradeRequests = async (status = 'pending') => {
  return client.get('intel/admin/upgrade-requests', { params: { status } });
};

/**
 * Admin: approve or reject an upgrade request.
 * @param {number} requestId
 * @param {object} body - { action: 'approve'|'reject', new_cap?: number,
 *                          admin_note?: string }
 */
export const resolveUpgradeRequest = async (requestId, body) => {
  return client.patch(`intel/admin/upgrade-requests/${requestId}`, body);
};

/**
 * Admin: list every org's credit plan, with primary email.
 * @param {string} search - case-insensitive email substring (optional)
 */
export const listOrgPlans = async (search = '') => {
  const params = search ? { search } : undefined;
  return client.get('intel/admin/org-plans', { params });
};

/**
 * Admin: internal LLM spend (coverage judge, etc.) for the last N days, with
 * revenue/margin context. Returns { total_cost_usd, total_calls, cached_calls,
 * by_model, by_purpose, by_customer, revenue_usd, margin_usd, credit_usd, ... }.
 */
export const listLlmCosts = async (days = 30) => {
  return client.get('intel/admin/llm-costs', { params: { days } });
};

/**
 * Admin: coverage gaps — per aspect, the signals with no real grounded source yet
 * (flows we still need to build). Returns { gaps: [{aspect_key, label, missing:[...]}] }.
 */
export const listCoverageGaps = async () => {
  return client.get('intel/admin/coverage-gaps');
};

/**
 * Judge-derived coverage action items for the org's own company on one aspect
 * (matched by visible label). Returns { aspect_key, coverage, grounded,
 * total_signals, recommendations: [{title, method, flow_key, credit_cost,
 * expected_coverage_gain, priority, missing_keys, rationale}] }.
 */
export const getCoverageRecommendations = async (label) => {
  return client.get('intel/coverage-recommendations', { params: { label } });
};

/**
 * Data-grounded, LLM-generated action plan for the org's own company on one aspect
 * (matched by visible label). Pass force=true to regenerate (the re-compare button).
 * Returns { aspect_key, label, plan: { summary, items[], data_gaps[] } | null }.
 */
export const getAspectActionPlan = async (label, force = false) => {
  return client.get('intel/aspect-action-plan', { params: force ? { label, force: 1 } : { label } });
};

/**
 * All aspects for the org with their CACHED action plans flattened — powers the
 * Action Items hub. Read-only; ungenerated/stale aspects have items:[] + status.
 * Returns { aspects: [{aspect_key, label, items, data_gaps, generated_at, version,
 * generated, stale}], planner_version }.
 */
export const getOrgActionItems = async () => {
  return client.get('intel/org-action-items');
};

/**
 * Task board: persistent, user-movable action tasks across all aspects, plus per-aspect
 * meta for the 'Run now' control (7-day cooldown).
 * Returns { aspects: [{aspect_key, label, generated, generated_at, stale, can_rerun,
 *   next_rerun_in_days, evidence}], tasks: [{id, aspect_key, aspect_label, title, impact,
 *   effort, rationale, basis, steps, table, status, created_at, status_changed_at}],
 *   planner_version, cooldown_days }.
 */
export const getActionTasks = async () => {
  return client.get('intel/action-tasks');
};

/** Move a task between statuses: 'new' | 'in_progress' | 'done'. */
export const updateActionTaskStatus = async (taskId, status) => {
  return client.patch(`intel/action-tasks/${taskId}`, { status });
};

/**
 * Raw grounding signals behind one aspect's recommendations, per company, WITH
 * provenance (which flow produced each value, the source URL, when it was observed).
 * Org-scoped on the backend — never exposes another org's private-integration data.
 * Returns { aspect_key, sources: [{ name, is_you, score, signals: [...] }] }.
 */
export const getAspectRawSources = async (aspectKey) => {
  return client.get('intel/aspect-raw-sources', { params: { aspect: aspectKey } });
};

/**
 * The FULL raw scan for one company — every field the flow returned (the entire latest
 * snapshot + analysis, no truncation, no field removal). Powers the "View full scan" page.
 */
export const getCompanyScan = async (companyId) => {
  return client.get('intel/company-scan', { params: { company_id: companyId } });
};

// ---- Social (Reddit) post-generation workflow ----
export const getSocialOverview = async () => client.get('intel/social/overview');
export const addSocialCommunity = async (body) => client.post('intel/social/communities', body);
export const bulkAddSocialCommunities = async (text) => client.post('intel/social/communities/bulk', { text });
export const updateSocialCommunity = async (id, body) => client.patch(`intel/social/communities/${id}`, body);
export const deleteSocialCommunity = async (id) => client.delete(`intel/social/communities/${id}`);
export const fetchSocialRules = async (id) => client.post(`intel/social/communities/${id}/fetch-rules`, {});
export const refreshSocialPages = async () => client.post('intel/social/refresh-pages', {});
export const generateSocialPosts = async (body) => client.post('intel/social/generate', body || {});
export const runSocial = async (body) => client.post('intel/social/run', body || {});
export const getSocialRunStatus = async () => client.get('intel/social/run-status');
export const excludeSocialPages = async (refs, excluded) => client.post('intel/social/pages/exclude', { refs, excluded });
export const updateSocialSuggestion = async (id, status, feedback) =>
  client.patch(`intel/social/suggestions/${id}`, feedback != null ? { status, feedback } : { status });
// Manual post logging + outcome (did it work?)
export const addSocialPost = async (body) => client.post('intel/social/posts', body);
export const setSocialPostOutcome = async (id, outcome) =>
  client.patch(`intel/social/suggestions/${id}`, { outcome });
// Edit a manual post (content + draft/ready/published lifecycle)
export const updateSocialPost = async (id, body) => client.patch(`intel/social/posts/${id}`, body);
// Turn an AI suggestion into an editable manual draft
export const draftFromSocialSuggestion = async (id) =>
  client.post(`intel/social/posts/from-suggestion/${id}`, {});
// Assist: rank the org's subreddits for a draft (+ rule flags); fix grammar/tone.
export const rankSocialCommunities = async (body) => client.post('intel/social/rank', body);
export const fixSocialGrammar = async (body) => client.post('intel/social/grammar', body);
// Comment templates (reusable snippets + win/loss tally)
export const addSocialTemplate = async (body) => client.post('intel/social/templates', body);
export const updateSocialTemplate = async (id, body) => client.patch(`intel/social/templates/${id}`, body);
export const deleteSocialTemplate = async (id) => client.delete(`intel/social/templates/${id}`);
export const recordSocialTemplateResult = async (id, success) =>
  client.post(`intel/social/templates/${id}/result`, { success });

/**
 * Real per-aspect detail for the action tab header: own current score/coverage,
 * weekly score history, and every company's current score (for rank + compare).
 * Returns { aspect_key, you, history: [{week_start, score}], competitors: [...] }.
 */
export const getAspectDetail = async (label) => {
  return client.get('intel/aspect-detail', { params: { label } });
};

/**
 * Real per-aspect score history for the org's own company (the weekly log) — powers
 * the Progress page. Returns { rows: [{aspect_key, label, series, start, current, gain, startDate, points}] }.
 */
export const getProgressData = async () => {
  return client.get('intel/progress');
};

/**
 * Admin: directly bump an org's cap or flip is_test_account.
 * Provide either email OR org_id, plus new_cap and/or is_test_account.
 * @param {object} body - { email?, org_id?, new_cap?, is_test_account? }
 */
export const adminBumpOrg = async (body) => {
  return client.post('intel/admin/bump-org', body);
};

/**
 * Admin: read current safety-limit values (e.g. max_admin_cap_per_bump).
 * Returns { max_admin_cap_per_bump: { effective, db_value, env_value,
 *                                      default, source, minimum_allowed } }
 */
export const getSafetyLimits = async () => {
  return client.get('intel/admin/safety-limits');
};

/**
 * Admin: update one or more safety-limit values.
 * @param {object} body - e.g. { max_admin_cap_per_bump: 8000 }
 */
export const updateSafetyLimits = async (body) => {
  return client.patch('intel/admin/safety-limits', body);
};

/**
 * Admin: global kill switch for ONE org — when paused, every Evaligo (spending)
 * flow is refused for that org regardless of budget or test-account status.
 * @param {object} body - { org_id?, email?, paused: boolean }
 */
export const adminPauseSpending = async (body) => {
  return client.post('intel/admin/pause-spending', body);
};

/** Admin: read platform-wide scheduled-job switches. -> { weekly_comparison_enabled, scheduler_auto_run } */
export const getJobControls = async () => {
  return client.get('intel/admin/job-controls');
};

/** Admin: all on-demand jobs + cooldown state (+ optional org_id for account-job state). */
export const getAdminJobs = async (orgId) =>
  client.get('intel/admin/jobs' + (orgId ? `?org_id=${orgId}` : ''));
/** Admin: trigger a job. body = { org_id?, force? }. */
export const runAdminJob = async (key, body) => client.post(`intel/admin/jobs/${key}/run`, body || {});
/** Customer: account jobs I can run for my org + gap state. */
export const getMyJobs = async () => client.get('intel/jobs');
/** Customer: run one of my account jobs now. */
export const runMyJob = async (key) => client.post(`intel/jobs/${key}/run`, {});

/**
 * Admin: stop/resume the weekly comparison jobs (public + per-org).
 * @param {object} body - { weekly_comparison_enabled: boolean }
 */
export const updateJobControls = async (body) => {
  return client.patch('intel/admin/job-controls', body);
};

// ============================================
// COMPETITOR COMPARISONS (in-platform)
// ============================================

/**
 * List comparisons owned by the current organization.
 * @returns {Promise} { comparisons: [...] }
 */
export const getOrgComparisons = async () => {
  return client.get('intel/comparisons');
};

/**
 * Get a single comparison by id (own or public).
 * @param {number} comparisonId
 * @returns {Promise} comparison detail
 */
export const getOrgComparisonDetail = async (comparisonId) => {
  return client.get(`intel/comparison/${comparisonId}`);
};

/**
 * Check whether a new manual comparison run is allowed right now.
 * @returns {Promise} { allowed, plan_name, cooldown_seconds, last_run_at,
 *                      next_allowed_at, seconds_until_next, min_companies, max_companies }
 */
export const getComparisonEligibility = async () => {
  return client.get('intel/comparison/eligibility');
};

/**
 * Trigger a new comparison.
 * @param {number[]} companyIds - 2 to 3 company ids the org tracks
 * @param {string} [title]
 * @returns {Promise} { status: 'success', comparison_id, slug, ... } or error
 */
export const runOrgComparison = async (companyIds, title = null) => {
  return client.post('intel/comparison/run', {
    company_ids: companyIds,
    title,
  });
};

export default {
  // Demo
  startDemoAnalysis,
  getDemoAnalysisResult,
  // Tracked competitors
  getTrackedCompetitors,
  addTrackedCompetitor,
  removeTrackedCompetitor,
  updateTrackedCompetitor,
  // Analysis
  getCompetitorAnalysis,
  getCompetitorTimeline,
  getCompetitorPricingHistory,
  getCompetitorTrends,
  refreshCompanyAnalysis,
  getScanStatus,
  // Feed
  getIntelligenceFeed,
  markFeedItemRead,
  dismissFeedItem,
  getUnreadCount,
  // Battlecards
  getBattlecard,
  regenerateBattlecard,
  getAllBattlecards,
  shareBattlecard,
  exportBattlecard,
  // Dashboard
  getIntelligenceDashboard,
  getHighImpactChanges,
  // Search / Discovery
  searchCompanies,
  discoverCompetitorSuggestions,
  updateMyCompanyDomain,
  // Onboarding
  discoverCompetitors,
  completeOnboardingSetup,
  getOnboardingStatus,
  analyzeAllCompetitors,
  getPlanInfo,
  // LinkedIn
  getLinkedInPosts,
  generateLinkedInPosts,
  updateLinkedInPostStatus,
  // Comparisons
  getOrgComparisons,
  getOrgComparisonDetail,
  getComparisonEligibility,
  runOrgComparison,
  // Credit tracking
  getCreditUsage,
  requestBetaUpgrade,
  listUpgradeRequests,
  resolveUpgradeRequest,
  listOrgPlans,
  listLlmCosts,
  adminBumpOrg,
  getSafetyLimits,
  updateSafetyLimits,
};

// ============================================
// GEO / AI VISIBILITY (value-first home)
// ============================================

/**
 * One-call summary for the AI Visibility page: own company + tracked
 * competitors with latest geo.answer_presence / geo.citation_rate,
 * per-engine breakdown, probed queries, last run time, running flag.
 */
export const getGeoSummary = async () => {
  return client.get('intel/geo/summary');
};

/**
 * Run the AI-visibility probe now (same path as the weekly job:
 * auto-built buyer queries -> ChatGPT/Gemini/Claude + Google SERP).
 * 202 {status:'started'|'already_running'} or 400
 * {error:'insufficient_data'|'no_own_company'}.
 */
export const startGeoProbe = async () => {
  return client.post('intel/geo/probe-now', {});
};
